import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Copy, Loader2, ArrowRight, Hash } from "lucide-react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { rememberPayment } from "./MyCodes";
import { toast } from "sonner";

export default function CheckoutReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [code, setCode] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [challengeId, setChallengeId] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setCode(data.code);
          setEmailSent(Boolean(data.email_sent));
          setChallengeId(data.challenge_id);
          // Persist for the "Mes codes" dashboard. Skip the lookup if the
          // purchase was generic (no challenge_id).
          if (data.challenge_id) {
            try {
              const { data: ch } = await api.get(
                `/challenges/public/${data.challenge_id}`,
              );
              rememberPayment({
                session_id: sessionId,
                challenge_id: data.challenge_id,
                challenge_title: ch.title,
                code: data.code,
              });
            } catch {
              rememberPayment({
                session_id: sessionId,
                challenge_id: data.challenge_id,
                code: data.code,
              });
            }
          } else {
            rememberPayment({
              session_id: sessionId,
              challenge_id: null,
              code: data.code,
              challenge_title: "Code de participation",
            });
          }
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current > 30) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        attemptsRef.current += 1;
        if (attemptsRef.current > 5) {
          setStatus("error");
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const copy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Code copié !");
    } catch {
      toast.error("Impossible de copier");
    }
  };

  return (
    <Layout>
      <div className="max-w-xl mx-auto px-6 py-16">
        <div className="panel rounded-3xl p-8 text-center scanlines relative">
          {status === "polling" && (
            <>
              <Loader2 className="w-12 h-12 mx-auto text-cyan-400 animate-spin mb-4" />
              <h1 className="font-display text-2xl font-bold mb-2">Confirmation du paiement...</h1>
              <p className="text-white/60 text-sm">On vérifie ta transaction avec Stripe, ça prend quelques secondes.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-400 mb-4" />
              <h1 className="font-display text-3xl font-black uppercase mb-1 neon-text-primary text-cyan-300">
                Paiement confirmé
              </h1>
              <p className="text-white/60 text-sm mb-6">Voici ton code personnel. Conserve-le précieusement.</p>
              {emailSent && (
                <div
                  data-testid="email-sent-note"
                  className="mb-6 flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-400/15 border border-emerald-400/30 text-emerald-300 text-xs uppercase tracking-widest font-mono-stat justify-center"
                >
                  📨 Un email avec ton code t'a été envoyé
                </div>
              )}
              <div
                data-testid="code-display"
                className="relative rounded-2xl border border-cyan-400/40 bg-black/50 p-6 mb-4 glow-primary"
              >
                <div className="text-xs uppercase tracking-[0.3em] text-cyan-300 font-mono-stat mb-2">Ton code</div>
                <div className="font-mono-stat font-black text-4xl sm:text-5xl tracking-[0.3em] text-white">
                  {code}
                </div>
                <button
                  data-testid="copy-code-button"
                  onClick={copy}
                  className="absolute top-3 right-3 p-2 rounded-lg bg-white/5 hover:bg-cyan-400/20 transition-all"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <button
                data-testid="checkout-play-now-button"
                onClick={() => navigate(`/play?code=${code}`)}
                className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl bg-cyan-400 text-black font-display font-bold uppercase tracking-wider hover:bg-cyan-300 active:scale-95 transition-all glow-primary"
              >
                <Hash className="w-4 h-4" />
                Utiliser mon code
                <ArrowRight className="w-5 h-5" />
              </button>
              {challengeId && (
                <button
                  onClick={() => navigate(`/challenges/${challengeId}`)}
                  className="mt-3 text-xs uppercase tracking-widest text-white/40 hover:text-white/70 font-mono-stat"
                >
                  Retour au défi
                </button>
              )}
            </>
          )}
          {(status === "expired" || status === "timeout" || status === "error") && (
            <>
              <h1 className="font-display text-2xl font-bold mb-3 text-pink-300">
                {status === "expired" ? "Session expirée" : "Vérification échouée"}
              </h1>
              <p className="text-white/60 text-sm mb-6">
                Si le paiement a réussi, retente ou contacte le support avec ton numéro de session.
              </p>
              <button
                onClick={() => navigate("/")}
                className="px-5 py-3 rounded-xl bg-white/10 hover:bg-white/20 font-display uppercase tracking-widest"
              >
                Retour à l'accueil
              </button>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
